import {
  type AccentHue,
  color,
  getHueColor,
} from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import {
  Chart,
  Colors,
  LinearScale,
  Tooltip,
  type TooltipModel,
} from "chart.js"
import { format } from "date-fns"
import { isDeepEqual } from "remeda"
import { Bar } from "solid-chartjs"
import {
  createMemo,
  createSignal,
  Match,
  onMount,
  Show,
  Switch,
} from "solid-js"
import NumberFlow, { type Format } from "solid-number-flow"
import { Box } from "../components/box"
import { Icon } from "../components/icon"
import {
  chartContainerRecipe,
  chartRecipe,
  dateClass,
  dateContainerClass,
  dateRangeClass,
  deltaRecipe,
  iconClass,
  statNumClass,
  statRecipe,
  statsHeaderClass,
  titleClass,
  titleLabelClass,
} from "./stats.css"

type StatsData = {
  count: number
  previousCount: number
  chartData?: {
    date: string
    count: number
  }[]
  intervalType: "HOUR" | "DAY"
}

type Props = {
  data?: StatsData
  hue: AccentHue
  iconName: IconName
  label: string
  loading: boolean
  format?: Format
}

export function Stats(props: Props) {
  onMount(() => {
    Chart.register(Tooltip, Colors, LinearScale)
  })

  const deltaPercentage = createMemo(() => {
    const stats = props.data
    if (!stats) return 0

    const views = stats.count
    const viewsPrevious = stats.previousCount
    if (viewsPrevious === 0 && views === 0) return 0
    if (viewsPrevious === 0) return Number.POSITIVE_INFINITY

    return Number((((views - viewsPrevious) / viewsPrevious) * 100).toFixed(1))
  })

  const bigNumber = createMemo(() => props.data?.count ?? 0)

  const hasChartData = createMemo(
    () => (props.data?.chartData?.length ?? 0) > 0,
  )

  const chartData = createMemo(
    () => {
      const stats = props.data
      if (!stats?.chartData) return null

      const intervalType = stats.intervalType

      const labels = stats.chartData.map(row => {
        if (intervalType === "HOUR") {
          return format(row.date, "p")
        }

        return format(row.date, "PP")
      })
      const data = stats.chartData.map(row => row.count ?? 0)

      return {
        labels,
        datasets: [
          {
            label: props.label,
            data,
            minBarLength: 2,
            backgroundColor: getHueColor(props.hue)("300"),
            hoverBackgroundColor: getHueColor(props.hue)("500"),
          },
        ],
      }
    },
    null,
    { equals: isDeepEqual },
  )

  const getDateRange = createMemo(() => {
    const stats = props.data
    if (!stats?.chartData) return null

    const firstDate = stats.chartData[0]?.date
    const lastDate = stats.chartData[stats.chartData.length - 1]?.date
    if (!firstDate || !lastDate) return null

    const intervalType = stats.intervalType
    const formatStr = intervalType === "HOUR" ? "p" : "PP"

    return {
      start: format(new Date(firstDate), formatStr),
      end: format(new Date(lastDate), formatStr),
    }
  })

  const [hoveredValue, setHoveredValue] = createSignal<number | null>(null)
  const [hoveredLabel, setHoveredLabel] = createSignal<string | null>(null)

  const displayDate = createMemo(() => {
    const hovered = hoveredLabel()
    if (hovered) return hovered

    const dateRange = getDateRange()
    return dateRange?.end ?? null
  })

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    color: color.sand800,
    borderRadius: 2,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
        external: (context: { tooltip: TooltipModel<"bar"> }) => {
          const tooltip = context.tooltip
          if (tooltip.opacity === 0) {
            setHoveredValue(null)
            setHoveredLabel(null)
            return
          }

          const dataPoint = tooltip.dataPoints?.[0]
          if (!dataPoint || !dataPoint.label) return

          const value = dataPoint.parsed?.y ?? dataPoint.raw
          const label = dataPoint.label
          setHoveredValue(typeof value === "number" ? value : null)
          setHoveredLabel(label)
        },
      },
    },
    layout: { padding: 0, autoPadding: false },
    scales: {
      x: { display: false },
      y: { display: false },
    },
  }
  const displayNumber = createMemo(() => hoveredValue() ?? bigNumber())

  return (
    <div class={statsHeaderClass}>
      <span class={titleClass}>
        <Icon
          name={props.iconName}
          size="24"
          class={iconClass({ hue: props.hue })}
        />
        <span class={titleLabelClass}>{props.label}</span>
      </span>
      <Stat
        loading={props.loading}
        number={displayNumber()}
        unit={props.label}
        delta={deltaPercentage()}
        showDelta={!hoveredLabel()}
        format={props.format}
      />
      <Show when={hasChartData()}>
        <div class={chartRecipe({ loading: props.loading })}>
          <div class={chartContainerRecipe({ loading: props.loading })}>
            <Show when={chartData()}>
              {chartData => (
                <Bar data={chartData()} options={chartOptions} height={50} />
              )}
            </Show>
          </div>
          <div class={dateContainerClass}>
            <div class={dateRangeClass({ show: !hoveredLabel() })}>
              <Show when={getDateRange()}>
                {dateRange => (
                  <>
                    <span>{dateRange().start}</span>
                    <span>{dateRange().end}</span>
                  </>
                )}
              </Show>
            </div>
            <span class={dateClass({ show: !!hoveredLabel() })}>
              {displayDate()}
            </span>
          </div>
        </div>
      </Show>
    </div>
  )
}

type StatProps = {
  number: number
  unit: string
  loading?: boolean
  delta: number
  showDelta: boolean
  format?: Format
}

function Stat(props: StatProps) {
  const trend = createMemo(() => {
    if (props.delta === 0) return "neutral"

    return props.delta > 0 ? "up" : "down"
  })

  return (
    <Box>
      <span class={statRecipe({ loading: props.loading })}>
        <span class={statNumClass}>
          <NumberFlow
            value={props.number}
            format={props.format ?? { notation: "compact" }}
            spinTiming={{ duration: 80, easing: "ease-in-out" }}
            transformTiming={{ duration: 60, easing: "ease-in-out" }}
            opacityTiming={{ duration: 100, easing: "ease-in-out" }}
          />
        </span>
        <span class={deltaRecipe({ trend: trend() })}>
          <Show
            when={props.showDelta && props.delta !== Number.POSITIVE_INFINITY}
          >
            <Switch>
              <Match when={trend() === "up"}>
                <Icon name="arrow-up" size="18" color="emerald600" />
              </Match>
              <Match when={trend() === "down"}>
                <Icon name="arrow-down" size="18" color="rose600" />
              </Match>
              <Match when={trend() === "neutral"}>
                <Icon name="equal" size="18" color="indigo600" />
              </Match>
            </Switch>
            {`${props.delta}%`}
          </Show>
        </span>
      </span>
    </Box>
  )
}
